/**
 * <swiper-carousel-latori> powers theme carousels using Swiper.
 *
 * By default it rides native scroll/scroll-snap (`cssMode: true`) with
 * `slidesPerView: "auto"`, keeping the existing grid markup and CSS
 * untouched. Mousewheel/trackpad scrolling works in this mode because the
 * wrapper is a real scrollable element.
 *
 * When `data-slides-mobile`, `data-slides-tablet` and `data-slides-desktop`
 * are all provided, it switches to Swiper's native fixed `slidesPerView` +
 * `breakpoints` instead, matching the theme's mobile (<768px), tablet
 * (768px-999px) and desktop (>=1000px) breakpoints. In that mode `cssMode`
 * is disabled so mousewheel/trackpad scrolling no longer moves the slides;
 * only touch and mouse click-drag do.
 *
 * Data attributes (all optional):
 * - data-pagination-type  "bullets" (default) or "fraction"
 * - data-page-label       Translated "Page" word, used when type is fraction
 * - data-of-label         Translated "of" word, used when type is fraction
 * - data-slides-mobile    Fixed slides per view below 768px
 * - data-slides-tablet    Fixed slides per view from 768px
 * - data-slides-desktop   Fixed slides per view from 1000px
 *
 * Expected markup:
 * <swiper-carousel-latori>
 *   <div class="swiper">
 *     <div class="swiper-wrapper">
 *       <div class="swiper-slide">...</div>
 *     </div>
 *   </div>
 *   <div class="slider-nav">
 *     <button name="prev">...</button>
 *     <div class="swiper-pagination"></div>
 *     <button name="next">...</button>
 *   </div>
 * </swiper-carousel-latori>
 *
 * A guard wraps the whole file, as in swatch-carousel-latori.js. Each section
 * that uses the carousel loads this script itself (recently-viewed-latori,
 * product-list, product-template-product-list). When two of them render on
 * one page, the second copy of this script must be a no-op. An unguarded
 * top-level class declaration throws a redeclaration SyntaxError.
 */
if (!customElements.get("swiper-carousel-latori")) {
  class SwiperCarouselLatori extends HTMLElement {
    connectedCallback() {
      this.swiperEl = this.querySelector(".swiper");
      this.slides = this.swiperEl
        ? [...this.swiperEl.querySelectorAll(".swiper-slide")]
        : [];

      if (this.slides.length < 2) {
        this.setAttribute("inactive", "");
        return;
      }

      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      if (this.hasAttribute("loaded") || typeof Swiper === "undefined") return;

      const prevBtn = this.querySelector('button[name="prev"]');
      const nextBtn = this.querySelector('button[name="next"]');
      const paginationEl = this.querySelector(".swiper-pagination");
      const paginationType = this.dataset.paginationType || "bullets";
      const pageLabel = this.dataset.pageLabel || "";
      const ofLabel = this.dataset.ofLabel || "/";

      const { slidesMobile, slidesTablet, slidesDesktop } = this.dataset;
      const hasFixedColumns = slidesMobile && slidesTablet && slidesDesktop;

      const layoutOptions = hasFixedColumns
        ? {
            cssMode: false,
            grabCursor: true,
            mousewheel: false,
            freeMode: {
              enabled: true,
              sticky: false,
              momentumBounce: false,
            },
            // slidesPerGroup = slidesPerView: the prev/next buttons and the
            // "Seite x von y" fraction move by a whole visible set (client
            // feedback: advancing by one card while the label said "page 2 of
            // 5" was confusing). Drag/wheel stays free (freeMode).
            breakpoints: {
              0: {
                slidesPerView: Number(slidesMobile),
                slidesPerGroup: Number(slidesMobile),
                spaceBetween: 16,
              },
              768: {
                slidesPerView: Number(slidesTablet),
                slidesPerGroup: Number(slidesTablet),
                spaceBetween: 20,
              },
              1000: {
                slidesPerView: Number(slidesDesktop),
                slidesPerGroup: Number(slidesDesktop),
                spaceBetween: 20,
              },
            },
          }
        : {
            cssMode: true,
            slidesPerView: "auto",
            spaceBetween: 0,
            freeMode: {
              enabled: true,
              sticky: false,
              momentumBounce: false,
            },
          };

      if (hasFixedColumns) this.swiperEl.style.userSelect = "none";

      this.swiper = new Swiper(this.swiperEl, {
        watchOverflow: true,
        watchSlidesProgress: true,
        a11y: true,
        ...layoutOptions,
        navigation:
          prevBtn && nextBtn ? { prevEl: prevBtn, nextEl: nextBtn } : false,
        pagination: paginationEl
          ? {
              el: paginationEl,
              type: paginationType,
              clickable: true,
              renderFraction: (currentClass, totalClass) =>
                `${pageLabel} <span class="${currentClass}"></span> ${ofLabel} <span class="${totalClass}"></span>`,
            }
          : false,
        on: {
          lock: () => this.setAttribute("inactive", ""),
          unlock: () => this.removeAttribute("inactive"),
        },
      });

      if (this.swiper.isLocked) this.setAttribute("inactive", "");

      this.setAttribute("loaded", "");
    }
  }

  customElements.define("swiper-carousel-latori", SwiperCarouselLatori);
}
